/**
 * Partições da fila por criticidade.
 */

import { shouldReplayItem } from '../replay/persistentReplay';
import type { QueueItem } from './types';

export type QueuePartition =
  | 'critical'
  | 'conversion'
  | 'behavior'
  | 'replay'
  | 'system';

export const FLUSH_ORDER: QueuePartition[] = [
  'critical',
  'conversion',
  'replay',
  'behavior',
  'system',
];

const CRITICAL_TYPES = new Set<string>(['PURCHASE', 'LEAD']);

export function isReplayPartitionCandidate(item: QueueItem): boolean {
  if (item.status === 'processing') return true;
  if (item.status !== 'queued' && item.status !== 'failed') return false;
  return (
    shouldReplayItem(item.params.sessionId, item.sessionSequence) &&
    item.attempts > 0
  );
}

export function getQueuePartition(item: QueueItem): QueuePartition {
  if (item.status === 'dead') return 'system';
  if (isReplayPartitionCandidate(item)) return 'replay';
  if (CRITICAL_TYPES.has(item.params.type) || item.priority === 'high') {
    return 'critical';
  }
  if (item.type === 'conversion') return 'conversion';
  return 'behavior';
}

export function partitionQueueItems(
  items: QueueItem[]
): Map<QueuePartition, QueueItem[]> {
  const map = new Map<QueuePartition, QueueItem[]>();
  for (const p of FLUSH_ORDER) map.set(p, []);
  for (const item of items) {
    const p = getQueuePartition(item);
    map.get(p)!.push(item);
  }
  return map;
}

export function flattenPartitionsInFlushOrder(
  partitioned: Map<QueuePartition, QueueItem[]>
): QueueItem[] {
  const out: QueueItem[] = [];
  for (const p of FLUSH_ORDER) {
    out.push(...(partitioned.get(p) ?? []));
  }
  return out;
}

export function getPartitionSizes(
  items: QueueItem[]
): Record<QueuePartition, number> {
  const sizes: Record<QueuePartition, number> = {
    critical: 0,
    conversion: 0,
    behavior: 0,
    replay: 0,
    system: 0,
  };
  for (const item of items) {
    sizes[getQueuePartition(item)]++;
  }
  return sizes;
}
